import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraTag, SpecraText } from '@mono/ui-components';
import { SpecraEmptyState, SpecraEntityCell, SpecraInlineBanner, SpecraPageHeader, SpecraRestrictedChip, SpecraSectionHeader, SpecraStatusPill, SpecraTabBar } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { proposalHref } from '../../../../lib/proposalHref';
import { categoryLabel, DOCUMENT_SECTIONS } from './categories';
import { BringInDialog, EditDialog, LibraryDialog, RemoveDialog, UploadDialog } from './ProposalDocumentDialogs';
import { documentErrorText, useProposalDocuments, useProposalDocumentWrites } from './useProposalDocuments';
const T = Testids.proposalDocuments;
const STATUS_LABEL = {
    processing: 'Processing',
    failed: 'Could not be processed',
    unreviewed_amendment: 'Amendment not reviewed',
    superseded: 'Replaced by a newer one'
};
function formatDate(iso) {
    const d = new Date(iso);
    return iso && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US') : '';
}
export function ProposalDocumentsPage() {
    const { id } = useParams();
    const [params, setParams] = useSearchParams();
    const projectId = id ?? '';
    const query = useProposalDocuments(projectId);
    const writes = useProposalDocumentWrites(projectId);
    const [dialog, setDialog] = useState(null);
    const section = params.get('section') ?? 'all';
    const data = query.data;
    const bySection = useMemo(()=>{
        const out = {};
        for (const d of data?.documents ?? []){
            out[d.section] = [
                ...out[d.section] ?? [],
                d
            ];
        }
        return out;
    }, [
        data
    ]);
    const counts = useMemo(()=>Object.fromEntries((data?.sections ?? []).map((s)=>[
                s.section,
                s.count
            ])), [
        data
    ]);
    useEffect(()=>{
        if (!data || section === 'all') return;
        document.getElementById(`proposal-documents-${section}`)?.scrollIntoView?.({
            block: 'start'
        });
    }, [
        data,
        section
    ]);
    if (!id) return null;
    const canUpload = data?.canUpload === true;
    const canUploadPricing = data?.canUploadPricingOnly === true;
    const pursuitRows = (bySection.pursuit ?? []).filter((d)=>d.artifactId !== '');
    const anyPending = Object.values(writes).some((m)=>m.isPending);
    const lastError = Object.values(writes).find((m)=>m.isError)?.error;
    const actions = data ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            canUpload ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": T.libraryButton,
                onClick: ()=>setDialog({
                        kind: 'library'
                    }),
                children: "Import from Library"
            }) : null,
            canUpload || canUploadPricing ? /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": T.uploadButton,
                onClick: ()=>setDialog({
                        kind: 'upload'
                    }),
                children: "Upload"
            }) : null
        ]
    }) : null;
    const select = (value)=>{
        const next = new URLSearchParams(params);
        if (value === 'all') next.delete('section');
        else next.set('section', value);
        setParams(next, {
            replace: true
        });
    };
    let body;
    if (query.isPending) {
        body = /*#__PURE__*/ _jsx("p", {
            "aria-busy": "true",
            "data-testid": T.loading,
            children: "Loading documents…"
        });
    } else if (query.isError) {
        body = /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            tone: "danger",
            role: "alert",
            "data-testid": T.error,
            action: /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                "data-testid": T.retry,
                onClick: ()=>query.refetch(),
                children: "Try again"
            }),
            children: [
                "Documents could not be loaded. ",
                documentErrorText(query.error)
            ]
        });
    } else if (!data || data.total === 0 && data.documents.length === 0) {
        body = /*#__PURE__*/ _jsx(SpecraEmptyState, {
            "data-testid": T.pageEmpty,
            title: "No documents yet",
            description: canUpload || canUploadPricing ? 'Upload the solicitation, your pricing and anything else this proposal needs.' : 'There are no documents you can see on this proposal yet.',
            action: canUpload || canUploadPricing ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>setDialog({
                        kind: 'upload'
                    }),
                children: "Upload"
            }) : undefined
        });
    } else {
        body = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraTabBar, {
                    label: "Document sections",
                    value: section,
                    onValueChange: select,
                    options: [
                        {
                            value: 'all',
                            label: 'All',
                            count: data.total,
                            testId: T.tab
                        },
                        ...DOCUMENT_SECTIONS.map((s)=>({
                                value: s.key,
                                label: s.label,
                                count: counts[s.key] ?? 0,
                                testId: T.tab
                            }))
                    ]
                }),
                DOCUMENT_SECTIONS.map((s)=>{
                    const rows = bySection[s.key] ?? [];
                    return /*#__PURE__*/ _jsxs("section", {
                        id: `proposal-documents-${s.key}`,
                        "data-testid": T.section,
                        "data-section": s.key,
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: s.label,
                                action: s.key === 'pursuit' && canUpload && pursuitRows.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    "data-testid": T.bringInButton,
                                    onClick: ()=>setDialog({
                                            kind: 'bring-in'
                                        }),
                                    children: "Bring in from the pursuit"
                                }) : undefined
                            }),
                            rows.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                "data-testid": T.sectionEmpty,
                                title: s.empty
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "divide-y",
                                children: rows.map((d)=>/*#__PURE__*/ _jsx(DocumentRow, {
                                        doc: d,
                                        projectId: projectId,
                                        canUpload: canUpload,
                                        busy: anyPending,
                                        onEdit: ()=>setDialog({
                                                kind: 'edit',
                                                doc: d
                                            }),
                                        onRemove: ()=>setDialog({
                                                kind: 'remove',
                                                doc: d
                                            }),
                                        onPackage: ()=>writes.setPackage.mutate({
                                                volumeDocumentId: d.volumeDocumentId,
                                                isSubmissionDoc: !d.inPackage
                                            })
                                    }, d.id))
                            })
                        ]
                    }, s.key);
                })
            ]
        });
    }
    const close = ()=>setDialog(null);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4 p-4 sm:p-6 lg:p-8",
        "data-testid": T.page,
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "ui",
                title: "Documents",
                description: "Everything for this proposal in one place: what the government sent, what we are writing, and what we are pulling in.",
                actions: actions
            }),
            lastError && !dialog ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                role: "alert",
                "data-testid": T.actionError,
                children: documentErrorText(lastError)
            }) : null,
            body,
            dialog?.kind === 'upload' ? /*#__PURE__*/ _jsx(UploadDialog, {
                writes: writes,
                canUpload: canUpload,
                onClose: close
            }) : null,
            dialog?.kind === 'library' ? /*#__PURE__*/ _jsx(LibraryDialog, {
                writes: writes,
                onClose: close
            }) : null,
            dialog?.kind === 'bring-in' ? /*#__PURE__*/ _jsx(BringInDialog, {
                writes: writes,
                candidates: pursuitRows,
                onClose: close
            }) : null,
            dialog?.kind === 'edit' ? /*#__PURE__*/ _jsx(EditDialog, {
                writes: writes,
                doc: dialog.doc,
                canUpload: canUpload,
                onClose: close
            }) : null,
            dialog?.kind === 'remove' ? /*#__PURE__*/ _jsx(RemoveDialog, {
                writes: writes,
                doc: dialog.doc,
                onClose: close
            }) : null
        ]
    });
}
function DocumentRow(p) {
    const d = p.doc;
    const isVolume = d.store === 'volume_document' && d.volumeId !== '';
    // Links to other places (SharePoint links, pursuit/Library candidates) have no
    // editable title or category here; only stored files do.
    const editable = d.canEdit && d.store === 'attachment';
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex flex-wrap items-center gap-3 py-2",
        "data-testid": T.row,
        "data-document-id": d.id,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "min-w-0 flex-1",
                children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: d.title,
                    description: d.filename && d.filename !== d.title ? d.filename : d.description
                })
            }),
            d.category ? /*#__PURE__*/ _jsx(SpecraTag, {
                children: categoryLabel(d.category)
            }) : null,
            d.versionLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                children: d.versionLabel
            }) : null,
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                children: [
                    d.originLabel,
                    formatDate(d.updatedAt)
                ].filter(Boolean).join(' · ')
            }),
            d.inPackage ? /*#__PURE__*/ _jsx(SpecraStatusPill, {
                status: "success",
                children: "In package"
            }) : null,
            STATUS_LABEL[d.status] ? /*#__PURE__*/ _jsx(SpecraStatusPill, {
                status: "warning",
                children: STATUS_LABEL[d.status]
            }) : null,
            d.sensitivity === 'legal_restricted' ? /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                "data-testid": T.restricted,
                label: "Restricted"
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-1",
                children: [
                    isVolume ? /*#__PURE__*/ _jsx(Link, {
                        "data-testid": T.open,
                        className: "text-sm underline",
                        to: `${proposalHref(p.projectId, `volume/${encodeURIComponent(d.volumeId)}`)}?doc=${encodeURIComponent(d.volumeDocumentId)}`,
                        children: "Open"
                    }) : null,
                    isVolume && d.canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.packageButton,
                        disabled: p.busy,
                        onClick: p.onPackage,
                        children: d.inPackage ? 'Take out of package' : 'Mark as package file'
                    }) : null,
                    editable ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.editButton,
                        disabled: p.busy,
                        onClick: p.onEdit,
                        children: "Change details"
                    }) : null,
                    d.canRemove ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": T.removeButton,
                        disabled: p.busy,
                        onClick: p.onRemove,
                        children: "Remove"
                    }) : null
                ]
            })
        ]
    });
}
